import React, { useState } from "react";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Flag, Loader2 } from "lucide-react";
import { toast } from "sonner";

export const REASONS = [
  ["harassment", "Harassment or bullying"], ["hate", "Hate or discrimination"], ["sexual", "Sexual content"], ["violence", "Violence or threats"],
  ["spam", "Spam or scam"], ["impersonation", "Impersonation"], ["confidential", "Leaks confidential Galleon matters"], ["other", "Something else"],
];

// Report dialog for posts / comments / messages / members → officers are alerted
export function ReportDialog({ target, onClose }) {
  const [reason, setReason] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  if (!target) return null;
  const send = async () => {
    setBusy(true);
    try { const { data } = await api.post("/reports", { target_type: target.type, target_id: target.id, reason, note }); toast.success(data.message || "Reported — the officers have been alerted"); onClose(); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="glass max-w-md" data-testid="report-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2 font-display"><Flag className="h-5 w-5 text-[hsl(var(--gold))]" />Report {target.type}</DialogTitle></DialogHeader>
        <p className="text-xs text-muted-foreground">Your report is private. The officers review every report and can remove the content or act on the member.</p>
        <div className="grid grid-cols-1 gap-1.5 sm:grid-cols-2">
          {REASONS.map(([k, label]) => <button key={k} type="button" onClick={() => setReason(k)} className={`rounded-lg border px-3 py-2 text-left text-xs transition-colors ${reason === k ? "border-[hsl(var(--gold))] bg-[hsl(var(--gold))]/15" : "border-border hover:border-[hsl(var(--gold))]/50"}`} data-testid={`report-reason-${k}`}>{label}</button>)}
        </div>
        <Textarea value={note} onChange={(e) => setNote(e.target.value)} placeholder="Anything the officers should know (optional)" rows={2} data-testid="report-note-input" />
        <Button onClick={send} disabled={!reason || busy} className="bg-[hsl(var(--gold))] font-700 text-[#0A192F]" data-testid="report-submit-button">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Send report"}</Button>
      </DialogContent>
    </Dialog>
  );
}

// Block / unblock helper shared by feed, directory and chat
export function useBlock() {
  const { user, setUser } = useAuth();
  const blocked = new Set(user?.blocked_ids || []);
  const block = async (id, name) => {
    if (!window.confirm(`Block ${name}? You will no longer see his posts, comments or messages, and he cannot message you. You can unblock him from Profile & Security.`)) return false;
    try { const { data } = await api.post(`/blocks/${id}`); setUser((u) => ({ ...u, blocked_ids: data.blocked_ids })); toast.success(`${name} blocked`); return true; }
    catch (e) { toast.error(errMsg(e)); return false; }
  };
  const unblock = async (id, name) => {
    try { const { data } = await api.delete(`/blocks/${id}`); setUser((u) => ({ ...u, blocked_ids: data.blocked_ids })); toast.success(`${name || "Member"} unblocked`); return true; }
    catch (e) { toast.error(errMsg(e)); return false; }
  };
  return { blocked, block, unblock, isBlocked: (id) => blocked.has(id) };
}
